import Button from '@viking-eng/button';
import Spinner from '@viking-eng/spinner';
import PropTypes from 'prop-types';

import './Booking.scss';
import { ENABLE_UNLOCK, FORMS, LOADING } from 'common/Constants';
import EditName from 'components/EditName/EditNameContainer';
import SignInAs from 'components/SignInAs/SignInAsContainer';
import { prepareHtml } from 'utils/strings';

const Booking = ({
  booking,
  clickedBookingId,
  handleActionClicked,
  handleUnlockClicked,
  isSubmitting,
  passengerInfo,
  passengerUpdateStatus,
  submitErrors,
  submitFailed,
  submitSucceeded,
}) => {
  const LABELS = {
    BOOKING_NUMBER: 'Booking Number',
    CRUISE_ID: 'Cruise ID',
    CRUISE_NAME: 'Cruise Name',
    EDIT_NAME: 'EDIT ACCOUNT NAME',
    GUEST_NAMES: 'Evolution Guest Names',
    SIGN_IN_AS: 'SIGN IN AS',
    UNLOCK_BOOKING: 'UNLOCK BOOKING',
  };

  if (isSubmitting) {
    return null;
  }

  if (submitSucceeded && booking?.[0]?.passengers) {
    return (
      <div>
        {booking?.map(({ bookingId, cruiseID, cruiseName, passengers, restricted }) => {
          let status;
          let actionsDisabled = false;
          const modalData = {
            user: passengers.length > 1 ? passengerInfo[0] : passengers[0],
          };
          return (
            <div className="row search-result-container" key={bookingId}>
              <div className="col-12">
                <div className="row">
                  <div className="col-2">
                    <div className="booking-summary-detail-wrapper">
                      <span className="booking-details-title small-body-copy d-block">{LABELS.BOOKING_NUMBER}</span>
                      <span className="booking-details-value medium-body-copy d-block">{bookingId}</span>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="booking-summary-detail-wrapper">
                      <span className="booking-details-title small-body-copy d-block">{LABELS.CRUISE_NAME}</span>
                      <span className="booking-details-value medium-body-copy d-block">{cruiseName}</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="booking-summary-detail-wrapper">
                      <span className="booking-details-title small-body-copy d-block">{LABELS.CRUISE_ID}</span>
                      <span className="booking-details-value medium-body-copy d-block">{cruiseID}</span>
                    </div>
                  </div>
                </div>
                <div className="guest-list">
                  <span className="title small-body-copy d-block ">{LABELS.GUEST_NAMES}</span>
                  <div className="row">
                    <div className="col-12">
                      {passengers.map((passenger) => {
                        if (
                          passengerUpdateStatus &&
                          passengerUpdateStatus.pax === passenger.pax &&
                          clickedBookingId === bookingId
                        ) {
                          status = <div className="status-message">{passengerUpdateStatus.message}</div>;
                          if (passengerUpdateStatus.message === LOADING) {
                            status = (
                              <div className="spinner-outer">
                                <Spinner />
                              </div>
                            );
                            actionsDisabled = true;
                          }
                        }
                        return (
                          <div className="row guest-row pb-3" key={passenger.pax}>
                            <div className="col-3 font-weight-bold">
                              {`${passenger.firstName} ${passenger.lastName}`}
                            </div>
                            <div className="col-9 d-flex align-items-center">
                              <div className="actions">
                                <Button
                                  appearance="link"
                                  attributes={{
                                    'data-dismiss': 'modal',
                                    'data-target': `#${FORMS.EDIT_NAME}`,
                                    'data-toggle': 'modal',
                                    disabled: actionsDisabled,
                                  }}
                                  onButtonClick={() => {
                                    handleActionClicked(passenger.pax, cruiseName, bookingId);
                                  }}
                                >
                                  {LABELS.EDIT_NAME}
                                </Button>
                                {!restricted && (
                                  <Button
                                    appearance="link"
                                    attributes={{
                                      'data-dismiss': 'modal',
                                      'data-target': `#${FORMS.SIGN_IN_AS}`,
                                      'data-toggle': 'modal',
                                      disabled: actionsDisabled,
                                    }}
                                    onButtonClick={() => handleActionClicked(passenger.pax, cruiseName, bookingId)}
                                  >
                                    {LABELS.SIGN_IN_AS}
                                  </Button>
                                )}
                                {ENABLE_UNLOCK && (
                                  <Button
                                    appearance="link"
                                    attributes={{
                                      disabled: actionsDisabled,
                                    }}
                                    onButtonClick={() => {
                                      handleUnlockClicked(booking.bookingId, passenger.pax);
                                    }}
                                  >
                                    {LABELS.UNLOCK_BOOKING}
                                  </Button>
                                )}
                              </div>
                              {passengerUpdateStatus && passengerUpdateStatus.pax === passenger.pax && status}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <EditName {...modalData} id={FORMS.EDIT_NAME} />
              <SignInAs {...modalData} id={FORMS.SIGN_IN_AS} />
            </div>
          );
        })}
      </div>
    );
  }

  if (submitFailed) {
    return (
      <div className="row search-result-container">
        <div className="col-12 no-result-container">
          <div className={submitErrors.type || 'no-result-text'} dangerouslySetInnerHTML={prepareHtml(submitFailed)} />
        </div>
      </div>
    );
  }

  return null;
};

Booking.propTypes = {
  booking: PropTypes.arrayOf(
    PropTypes.shape({
      passengers: PropTypes.arrayOf(PropTypes.shape({})),
      bookingId: PropTypes.string,
      cruiseID: PropTypes.string,
      cruiseName: PropTypes.string,
      restricted: PropTypes.bool,
    }),
  ),
  clickedBookingId: PropTypes.string,
  handleActionClicked: PropTypes.func.isRequired,
  handleUnlockClicked: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  passengerInfo: PropTypes.shape({}),
  passengerUpdateStatus: PropTypes.shape({
    pax: PropTypes.number,
    message: PropTypes.string,
  }),
  submitErrors: PropTypes.shape({
    type: PropTypes.string,
  }),
  submitFailed: PropTypes.string,
  submitSucceeded: PropTypes.bool.isRequired,
};

Booking.defaultProps = {
  booking: null,
  clickedBookingId: '',
  passengerInfo: null,
  passengerUpdateStatus: null,
  submitErrors: {},
  submitFailed: '',
};

export default Booking;
